import { onError } from '@apollo/client/link/error/index.js'
import { fromPromise } from '@apollo/client/link/utils/index.js'
import { failure } from '@mntn/result'
import { recordGaryFieldError, toErrorCode, toErrorPath } from '../../observability/gary-metrics.ts'

type ErrorLinkOptions = {
  refreshSession?: () => Promise<void>
}

/** Errors are reported through `failure()`, which writes to the registered global logger. */
export const errorLink = ({ refreshSession }: ErrorLinkOptions = {}) =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      const unauthenticatedError = graphQLErrors.find(e => e?.extensions?.code === 'UNAUTHENTICATED')

      /**
       * Gary doesn't throw the conventional apollo status "UNAUTHENTICATED".
       * Matching against Gary's custom string, though this is a brittle solution.
       * @todo - Check expiration date on cookie and refresh before unauthenticated state reached
       * @todo - Return Unauthenticated code from Gary
       */
      const isAuthGqlError = graphQLErrors?.some(
        e => e.extensions?.isFromGaryQL && e.extensions?.code === 200 && /authentic/i.test(e.message), // message contains “auth”
      )

      const alreadyRetried = operation.getContext().retried

      const shouldAttemptRefresh = isAuthGqlError || unauthenticatedError

      if (shouldAttemptRefresh && refreshSession && !alreadyRetried) {
        operation.setContext({ retried: true })

        return fromPromise(
          refreshSession().catch(err => {
            failure({
              customCode: 'GARY_UNAUTHENTICATED_ERROR',
              message: 'refreshSession failed',
              cause: err,
            })
          }),
        ).flatMap(() => forward(operation))
      }

      /**
       * Everything that reaches here is an error Gary returned inside a 200 — a failed
       * resolver rather than a failed transport. Apollo hands these to the caller as
       * partial data, so without this branch they leave no trace but a span status.
       * It sits below the refresh return so a retried session is not counted as a
       * downstream failure.
       */
      for (const gqlError of graphQLErrors) {
        const path = toErrorPath(gqlError.path)
        const code = toErrorCode(gqlError.extensions)

        recordGaryFieldError({
          operationName: operation.operationName,
          path,
          code,
        })

        failure({
          customCode: 'GARY_GRAPHQL_FIELD_ERROR',
          message: 'Gary GraphQL field error',
          payload: {
            operationName: operation.operationName,
            path,
            code,
            graphqlMessage: gqlError.message,
          },
        })
      }
    }

    if (networkError) {
      const statusCode = 'statusCode' in networkError ? networkError.statusCode : undefined
      const response = 'response' in networkError ? networkError.response : undefined

      const error = {
        customCode: 'GARY_NETWORK_ERROR',
        message: 'Gary network request failed',
        payload: {
          statusCode,
          operationName: operation.operationName,
          url: response?.url,
          networkMessage: networkError.message,
        },
      } as const

      failure(error, { variables: operation.variables })
    }
  })
